import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Container, Typography, Link } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { trackError } from '../lib/analytics';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Track error in Google Analytics
    trackError(
      'React Error Boundary',
      `${error.name}: ${error.message} | Component Stack: ${errorInfo.componentStack?.substring(0, 100)}`,
      true // fatal error
    );
  }

  private resetError = () => {
    this.setState({ hasError: false, error: undefined });
  };

  public render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <Container maxWidth="sm">
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                textAlign: 'center',
                gap: 3,
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 80, color: 'error.main' }} />
              <Typography variant="h4" component="h1" gutterBottom>
                Oops! Something went wrong
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                We're sorry for the inconvenience. An unexpected error has occurred.
              </Typography>
              {this.state.error && process.env.NODE_ENV === 'development' && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    width: '100%',
                    textAlign: 'left',
                  }}
                >
                  <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                    {this.state.error.message}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={this.resetError}
                    size="large"
                  >
                    Try Again
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => window.location.reload()}
                    size="large"
                  >
                    Refresh Page
                  </Button>
                </Box>

                <Typography variant="caption" color="text.secondary">
                  If the problem persists, please{' '}
                  <Link href="/contact" underline="hover" color="primary">
                    contact support
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Container>
        )
      );
    }

    return this.props.children;
  }
}
